import { useEffect, useState } from 'react';
import { LuChevronRight } from 'react-icons/lu';
import useCustomers from '../../../../hooks/useCustomers';
import { Loader } from '@piggytechng/component-library';
import dayjs from 'dayjs';
import NoData from '../../../../components/NoData/NoData';
import SideSheet from '../../../../components/NewSideSheet';
import Pagination from '../../../../components/Pagination/Pagination';

export default function FlagList({ userId }: any) {
  const [flags, setFlags] = useState<any[]>([]);
  const [selectedFlag, setSelectedFlag] = useState<any | null>(null);
  const [paginationParams, setPaginationParams] = useState({ page: 1, limit: 30 });
  const { getFlagHistory, isFetchingFlagHistory } = useCustomers();

  useEffect(() => {
    const fetchHistory = async () => {
      const data = await getFlagHistory(userId, paginationParams.page, paginationParams.limit);
      if (data) {
        setFlags(data.results);
      }
    };
    fetchHistory();
  }, [userId, paginationParams.page, paginationParams.limit]);

  const detailRows: { label: string; key: string }[] = [
    { label: 'Flag ID', key: 'id' },
    { label: 'Type', key: 'type' },
    { label: 'Memo', key: 'memo' },
    { label: 'Date Created', key: 'date' },
    { label: 'Admin', key: 'admin' },
    { label: 'Reason', key: 'reason' },
    { label: 'Status', key: 'status' },
    { label: 'Reference', key: 'reference' },
  ];

  return (
    <div className="flag-list">
      {isFetchingFlagHistory ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
          <Loader color="#0F60D6" />
        </div>
      ) : flags.length === 0 ? (
        <NoData message="No flag history found." />
      ) : (
        <div className="table-container flaglist">
          <table className="table">
            <thead className="table_head">
              <tr>
                <th className="id">Flag ID</th>
                <th className="type">Type</th>
                <th className="memo">Memo</th>
                <th className="date">Date Created</th>
                <th />
              </tr>
            </thead>
            <tbody className="table-body">
              {flags.map((flag: any) => (
                <tr key={flag.id} className="table-row" onClick={() => setSelectedFlag(flag)}>
                  <td className="id">{flag.id}</td>
                  <td className="type">{flag.type ? flag.type : 'N/A'}</td>
                  <td className="memo">{flag.memo ? flag.memo : 'N/A'}</td>
                  <td className="date">{dayjs(flag.date * 1000).format('D MMM, YYYY HH:mm:ss')}</td>
                  <td style={{ color: '#94a3b8', paddingRight: '16px' }}>
                    <LuChevronRight size={18} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        currentPage={paginationParams.page}
        hasNextPage={flags.length >= paginationParams.limit}
        hasPrevPage={paginationParams.page > 1}
        onNextPage={() => setPaginationParams((prev) => ({ ...prev, page: prev.page + 1 }))}
        onPrevPage={() => setPaginationParams((prev) => ({ ...prev, page: Math.max(prev.page - 1, 1) }))}
        limit={paginationParams.limit}
        onLimitChange={(newLimit) => setPaginationParams({ page: 1, limit: newLimit })}
      />

      <SideSheet
        active={!!selectedFlag}
        onClose={() => setSelectedFlag(null)}
        title="Flag Details"
        subTitle={selectedFlag ? `Flag #${selectedFlag.id}` : ''}
      >
        {selectedFlag && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '8px 0' }}>
            {detailRows.map(({ label, key }) => {
              let value = selectedFlag[key];
              if (!value && value !== 0) return null;
              if (key === 'date') {
                value = dayjs(value * 1000).format('D MMM, YYYY HH:mm:ss');
              }
              return (
                <div
                  key={key}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    borderBottom: '1px solid #f1f5f9',
                    paddingBottom: '16px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: '#94a3b8',
                    }}
                  >
                    {label}
                  </span>
                  <span
                    style={{
                      fontSize: '14px',
                      color: '#1e293b',
                      fontWeight: key === 'id' ? 600 : 400,
                      wordBreak: 'break-word',
                      lineHeight: 1.6,
                    }}
                  >
                    {String(value)}
                  </span>
                </div>
              );
            })}

            {/* Render any extra fields not in the predefined list */}
            {Object.entries(selectedFlag)
              .filter(([key]) => !detailRows.some((r) => r.key === key))
              .filter(([, val]) => val !== null && val !== undefined && val !== '')
              .map(([key, val]) => (
                <div
                  key={key}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    borderBottom: '1px solid #f1f5f9',
                    paddingBottom: '16px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: '#94a3b8',
                    }}
                  >
                    {key.replace(/_/g, ' ')}
                  </span>
                  <span style={{ fontSize: '14px', color: '#1e293b', wordBreak: 'break-word', lineHeight: 1.6 }}>
                    {String(val)}
                  </span>
                </div>
              ))}
          </div>
        )}
      </SideSheet>
    </div>
  );
}
